"use client";

import { TypeListItemCollection } from "@contentful/types";
import { notEmpty } from "@yardzen/js-util";
import { Badge } from "@yardzen/ui/components/badge";
import { H1 } from "@yardzen/ui/components/h1";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { ListItem } from "./ListItem";
import { ListItemCollectionAccordian } from "./ListItemCollectionAccordian";

export function ListItemCollection({
  listItemCollection,
  className,
  innerClassName,
  listItemsClassName,
  listItemsContainerClassName,
  listItemHeadingClassName,
  listItemSubHeadingClassName,
  listItemIconClassName,
  listItemIconWrapperClassName,
  iconSize,
  listItemsAtBottom,
  hideCollectionHeader,
  disableDynamicColumns,
  isModal,
}: {
  listItemCollection: TypeListItemCollection<"WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
  innerClassName?: string;
  listItemsClassName?: string;
  listItemsContainerClassName?: string;
  listItemHeadingClassName?: string;
  listItemSubHeadingClassName?: string;
  listItemIconClassName?: string;
  listItemIconWrapperClassName?: string;
  iconSize?: "sm" | "md" | "lg";
  listItemsAtBottom?: boolean;
  hideCollectionHeader?: boolean;
  disableDynamicColumns?: boolean;
  isModal?: boolean;
}) {
  const { listItems, eyebrow, heading, subheading, variant, interactivity } =
    listItemCollection.fields ?? {};

  const dynamicColumns =
    listItems && listItems.length > 1 && !disableDynamicColumns;

  const variantResolved = variant ?? "icon-left";
  const iconVariants = cva("", {
    variants: {
      columnClass: {
        "icon-above": "grid grid-cols-3 gap-4 max-md:grid-cols-2",
        "icon-above-large":
          "grid justify-center gap-4 grid-cols-2 lg:grid-cols-4 pt-12 pb-8 px-4",
        "icon-card": "mt-4 grid grid-cols-1 gap-4",
        "icon-left": `mt-0 grid gap-2 md:gap-1.5 pb-2 ${isModal ? "grid-cols-1" : listItems && listItems.length === 1 ? "grid-cols-1" : dynamicColumns ? "grid-cols-1 lg:grid-cols-1" : "lg:grid-cols-2"} ${listItemsAtBottom ? "h-full content-end" : ""}`,
        "icon-left-large":
          "mt-1 md:mt-8 grid gap-3 md:gap-4 pt-2 pb-6 grid-cols-1",
      },
      wrapperOuterClass: {
        "icon-above": "",
        "icon-above-large": "bg-[#e6ede6] mt-12",
        "icon-card": "",
        "icon-left": "",
        "icon-left-large": "",
      },
      wrapperInnerClass: {
        "icon-above": "flex flex-col items-center mt-0 mb-8 md:mb-10",
        "icon-above-large": "py-12 mx-auto max-w-[1200px] px-4",
        "icon-card": "flex flex-col lg:col-span-1 h-full",
        "icon-left": "flex flex-col lg:col-span-1 h-full",
        "icon-left-large": "flex flex-col lg:col-span-1",
      },
      headingClass: {
        "icon-above": "",
        "icon-above-large": "text-center",
        "icon-card": "",
        "icon-left": "",
        "icon-left-large": "text-4xl lg:text-4xl text-left",
      },
      subheadingClass: {
        "icon-above": "",
        "icon-above-large":
          "text-center text-lg leading-[170%] text-brand-moss-greens-90 px-4",
        "icon-card": "",
        "icon-left": "mb-3",
        "icon-left-large": "text-left",
      },
      itemContainerClass: {
        "icon-above": "",
        "icon-above-large": "flex flex-col flex-1 items-center",
        "icon-card": "flex flex-row items-center gap-4 rounded-lg p-3",
        "icon-left": "",
        "icon-left-large": "mb-2 flex flex-row items-center gap-4",
      },
      itemHeadingClass: {
        "icon-above": "",
        "icon-above-large": "text-center text-3xl font-display mt-6",
        "icon-card": "text-base font-display",
        "icon-left": "",
        "icon-left-large": "",
      },
      itemSubheadingClass: {
        "icon-above": "",
        "icon-above-large":
          "text-center text-lg pt-6 leading-[170%] text-brand-moss-greens-90",
        "icon-card": "text-sm text-brand-moss-greens-90",
        "icon-left": "",
        "icon-left-large": "",
      },
      itemIconWrapperClassName: {
        "icon-above": "",
        "icon-above-large": "p-6 bg-white border-black border-2",
        "icon-card":
          "p-3 !rounded-lg !bg-system-grayscale-10 !shadow-none shrink-0",
        "icon-left": "",
        "icon-left-large": "w-14 h-14 px-3 py-3 place-self-start",
      },
      itemIconClassName: {
        "icon-above": "",
        "icon-above-large": "h-[70px] w-[70px]",
        "icon-card": "h-[28px] w-[28px]",
        "icon-left": "",
        "icon-left-large": "h-[32px] w-[32px]",
      },
      badgeClass: {
        "icon-above":
          "bg-brand-moss-greens-10 my-4 w-fit bg-opacity-50 text-center text-base font-normal mx-auto",
        "icon-above-large": "",
        "icon-card":
          "bg-brand-moss-greens-10 my-4 w-fit bg-opacity-50 text-center text-base font-normal font-semibold md:flex",
        "icon-left":
          "bg-brand-moss-greens-10 my-4 w-fit bg-opacity-50 text-center text-base font-normal font-semibold md:flex",
        "icon-left-large":
          "bg-brand-moss-greens-10 my-4 w-fit bg-opacity-50 text-center text-base font-normal font-semibold md:flex",
      },
    },
  });

  return interactivity === "Display Image/Subheading on hover/click" ? (
    <ListItemCollectionAccordian
      listItemCollection={listItemCollection}
      listItemHeadingClassName={listItemHeadingClassName}
      listItemSubHeadingClassName={listItemSubHeadingClassName}
    />
  ) : (
    <div
      className={cn(
        iconVariants({ wrapperOuterClass: variantResolved }),
        className,
      )}
    >
      <div
        className={cn(
          iconVariants({ wrapperInnerClass: variantResolved }),
          innerClassName,
        )}
      >
        {eyebrow && (
          <Badge
            className={iconVariants({ badgeClass: variantResolved })}
            variant="secondary"
            hover={false}
          >
            {eyebrow}
          </Badge>
        )}
        {heading && !hideCollectionHeader && (
          <H1
            className={cn(
              "text-brand-primary-darker text-left",
              iconVariants({ headingClass: variantResolved }),
            )}
          >
            {heading}
          </H1>
        )}
        {subheading && (
          <P className={iconVariants({ subheadingClass: variantResolved })}>
            {subheading}
          </P>
        )}
        <div
          className={cn(
            iconVariants({ columnClass: variantResolved }),
            listItemsClassName,
          )}
        >
          {listItems &&
            listItems
              .filter(notEmpty)
              .map((listItem, i) => (
                <ListItem
                  key={`list-item-${i}`}
                  listItem={listItem}
                  variant={variantResolved}
                  iconClassName={cn(
                    listItemIconClassName,
                    iconVariants({ itemIconClassName: variantResolved }),
                  )}
                  iconWrapperClassName={cn(
                    listItemIconWrapperClassName,
                    iconVariants({ itemIconWrapperClassName: variantResolved }),
                  )}
                  iconSize={iconSize}
                  headingClassName={cn(
                    listItemHeadingClassName,
                    iconVariants({ itemHeadingClass: variantResolved }),
                  )}
                  subHeadingClassName={cn(
                    listItemSubHeadingClassName,
                    iconVariants({ itemSubheadingClass: variantResolved }),
                  )}
                  className={cn(
                    listItemsContainerClassName,
                    iconVariants({ itemContainerClass: variantResolved }),
                  )}
                />
              ))}
        </div>
      </div>
    </div>
  );
}
